import Phaser from "phaser";
import { AssetKeys } from "../../core/AssetKeys";
import { GameEvents } from "../../core/Events";
import { Player } from "../player/Player";
import { CharacterAnimator } from "../../animation/CharacterAnimator";
import { direction4FromAngle, type FourDirection } from "../../animation/Direction";

export class SkeletonKing extends Phaser.Physics.Arcade.Sprite {
  private readonly target: Player;
  private health = 25000;
  private readonly maxHealth = 25000;
  private phase = 1;
  private nextPatternAt = 0;
  private readonly animator: CharacterAnimator;
  private facing: FourDirection = "s";
  private defeated = false;

  constructor(scene: Phaser.Scene, x: number, y: number, target: Player) {
    super(scene, x, y, AssetKeys.SkeletonKingAtlas, "idle_s_000");
    this.target = target;

    scene.add.existing(this);
    scene.physics.add.existing(this);

    this.setImmovable(true);
    this.setScale(1.1);
    this.setDepth(Math.round(y));
    this.animator = new CharacterAnimator(this, "skeleton-king");

    const body = this.body as Phaser.Physics.Arcade.Body;
    body.setSize(56, 70);
    body.setOffset(20, 24);

    scene.events.emit(GameEvents.BossHealthChanged, this.health, this.maxHealth, this.phase);
  }

  override update(time: number): void {
    if (this.defeated || !this.target.active) return;

    const angle = Phaser.Math.Angle.Between(this.x, this.y, this.target.x, this.target.y);
    this.facing = direction4FromAngle(angle);
    this.animator.updateLocomotion(this.facing, false, time);

    if (time >= this.nextPatternAt) {
      this.nextPatternAt = time + (this.phase === 1 ? 3200 : this.phase === 2 ? 2500 : 1900);
      this.executePattern();
    }
  }

  damage(amount: number): void {
    if (this.defeated) return;

    this.health = Math.max(0, this.health - amount);
    const previousPhase = this.phase;

    if (this.health <= this.maxHealth * 0.33) this.phase = 3;
    else if (this.health <= this.maxHealth * 0.66) this.phase = 2;

    if (this.phase !== previousPhase) {
      this.scene.cameras.main.shake(280, 0.006);
      this.setTint(0xd8a4ff);
      this.scene.time.delayedCall(250, () => this.active && this.clearTint());
    }

    this.scene.events.emit(
      GameEvents.BossHealthChanged,
      this.health,
      this.maxHealth,
      this.phase,
    );

    if (this.health <= 0) {
      this.defeated = true;
      (this.body as Phaser.Physics.Arcade.Body).enable = false;
      this.animator.playAction("death", 1700, "s");
      this.scene.events.emit(GameEvents.BossDefeated);
      this.scene.time.delayedCall(1800, () => this.setVisible(false));
    } else {
      this.animator.playAction("hit", 220, this.facing);
    }
  }

  isDefeated(): boolean {
    return this.defeated;
  }

  private executePattern(): void {
    this.animator.playAction("cast", 1100, this.facing);
    this.scene.events.emit("boss-pattern", {
      x: this.x,
      y: this.y,
      phase: this.phase,
    });
  }
}
